import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AMAZON_AFFILIATE_TAG } from "../data/guides";

interface LegalDisclaimerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal con note legali, attribuzioni e disclaimer.
 *
 * Triggera l'apertura tramite il link "Note legali" sotto i filtri (vedi
 * MapComponent.tsx). Si chiude con click sul backdrop, sul bottone X o
 * con il tasto ESC.
 *
 * Il contenuto copre quattro aree:
 *  1) Attribuzioni editoriali (le 4 guide cui si fa riferimento)
 *  2) Cartografia OpenStreetMap
 *  3) Affiliazione Amazon
 *  4) AI Gemini + disclaimer di completezza/accuratezza
 *  5) Email di contatto per correzioni / richieste di rimozione
 */
export const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({ isOpen, onClose }) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // Chiusura con ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Blocca lo scroll del body mentre il modal e' aperto
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  // Render via portal su document.body: il modal e' renderizzato come figlio
  // di MapControls, che a sua volta vive dentro il container Leaflet. Senza
  // portal, gli eventi wheel/click del modal risalgono al MapContainer e
  // Leaflet li intercetta (zoom della mappa al posto dello scroll del modal).
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 id="legal-title" className="text-lg font-bold text-gray-900">
            Note legali e attribuzioni
          </h2>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 transition-colors p-1 -mr-1"
            aria-label="Chiudi"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-5">

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Selezione editoriale e fonti</h3>
            <p>
              La selezione dei locali presentati in Itinere (ristoranti, pizzerie,
              gelaterie, pasticcerie, bar, osterie e affini) deriva da guide editoriali
              pubblicate da editori terzi, riportate qui come citazione e segnalazione a
              beneficio dei lettori. I diritti editoriali su valutazioni, classifiche,
              tier (stelle, forchette, cappelli, chiocciole, coni, ecc.) e descrizioni
              rimangono di proprieta' dei rispettivi editori:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Guida Michelin</strong> &mdash; &copy; Michelin Group</li>
              <li><strong>Ristoranti d'Italia, Pizzerie d'Italia, Gelaterie d'Italia, Bar d'Italia, Pasticcerie d'Italia, Street Food d'Italia, Sushi &amp; altre cucine asiatiche</strong> &mdash; &copy; Gambero Rosso S.p.A.</li>
              <li><strong>Le Guide de L'Espresso &mdash; Ristoranti d'Italia</strong> &mdash; &copy; GEDI News Network / Editrice L'Espresso</li>
              <li><strong>Osterie d'Italia</strong> &mdash; &copy; Slow Food Editore</li>
            </ul>
            <p className="mt-3">
              Itinere <strong>non e' affiliato, sponsorizzato o approvato</strong> da
              nessuno degli editori citati. I nomi delle guide, i tier e le valutazioni
              sono utilizzati come elementi descrittivi a scopo di segnalazione e per
              consentire all'utente di approfondire sui rispettivi siti ufficiali.
              Le coordinate geografiche, i nomi dei locali e gli indirizzi sono dati
              fattuali raccolti dai listing pubblicamente disponibili e arricchiti
              tramite Nominatim &mdash; OpenStreetMap.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Cartografia</h3>
            <p>
              I tile della mappa sono forniti da{" "}
              <a className="text-cyan-600 hover:text-cyan-500 underline" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap
              </a>{" "}
              e dai suoi contributori, rilasciati sotto licenza Open Database License
              (ODbL). I dati di geocoding sono forniti dal servizio{" "}
              <a className="text-cyan-600 hover:text-cyan-500 underline" href="https://nominatim.openstreetmap.org/" target="_blank" rel="noopener noreferrer">
                Nominatim
              </a>.
              &copy; OpenStreetMap contributors.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Affiliazione Amazon</h3>
            <p>
              I link "Compra la guida" presenti accanto ad alcuni POI rimandano a
              ricerche su Amazon.it utilizzando il tag di affiliazione{" "}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{AMAZON_AFFILIATE_TAG}</code>.
              In conformita' con il Programma di Affiliazione Amazon EU, riceviamo una
              piccola commissione sugli acquisti idonei effettuati tramite questi link,
              senza costi aggiuntivi per l'utente.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Intelligenza artificiale</h3>
            <p>
              Alcune funzionalita' di scoperta (suggerimenti, descrizioni, ricerca
              semantica) utilizzano le API di Google Gemini. I dati trasmessi al
              modello sono limitati al minimo necessario alla query in corso e non
              includono identificativi personali permanenti.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Limitazione di responsabilita'</h3>
            <p>
              Le informazioni qui presentate (orari, indirizzi, numeri di telefono,
              siti web, prezzi indicativi) potrebbero risultare imprecise, incomplete
              o non aggiornate. Si raccomanda di <strong>verificare sempre direttamente
              con il locale</strong> prima di pianificare una visita o di considerare
              affidabile uno specifico dato.
            </p>
            <p className="mt-2">
              Itinere e' offerto "cosi' com'e'" (<em>as is</em>) senza garanzie
              esplicite o implicite di completezza, accuratezza o idoneita' a uno
              specifico scopo. L'uso e' a esclusivo rischio dell'utente.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Privacy Policy e Termini d'uso</h3>
            <p>
              Per i documenti legali completi consulta:{" "}
              <a className="text-cyan-600 hover:text-cyan-500 underline" href="/privacy.html" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{" "}e{" "}
              <a className="text-cyan-600 hover:text-cyan-500 underline" href="/terms.html" target="_blank" rel="noopener noreferrer">
                Termini e Condizioni
              </a>.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">Segnalazioni e contatti</h3>
            <p>
              Per segnalare errori, richiedere la rimozione di un locale, o per
              qualunque altra richiesta relativa ai contenuti di Itinere:{" "}
              <a className="text-cyan-600 hover:text-cyan-500 underline" href="mailto:lucatorri@gmail.com">
                lucatorri@gmail.com
              </a>.
            </p>
          </section>

        </div>

        <footer className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3 text-xs text-gray-500 text-center">
          Ultimo aggiornamento: maggio 2026
        </footer>
      </div>
    </div>,
    document.body
  );
};
